import type { ParseFlowViewMode } from '@/types/parse-flow-file';
import type { ActiveAISessionStage } from '@/stores/uiStore';
import {
  isCompletedStoryboardStage,
  isProtectedTransientAISessionStage,
} from '@/lib/ai-session/workflow/active-stage';

interface FooterButtonConfig {
  show: boolean;
  text: string;
  disabled: boolean;
  tooltip?: string;
}

interface ResolveParseFlowLayoutStateParams {
  viewMode: ParseFlowViewMode;
  activeScreenplayId: string | null;
  screenplayListCount: number;
  initialScript: string;
  shotsCount: number;
}

export interface ParseFlowLayoutState {
  isStoryboardWorkspaceMode: boolean;
  hasAnyScreenplay: boolean;
  hasAnyShots: boolean;
  showScriptPreviewPanel: boolean;
  showStoryboardListPanel: boolean;
  showStoryboardEditPanel: boolean;
  footerButtonConfig: FooterButtonConfig;
  showDialogFooter: boolean;
  dialogGridTemplateColumns: string;
}

export type ScreenplaySelectAISessionAction = 'none' | 'switch' | 'block' | 'exit';
export type EpisodeSwitchConfirmChoice = 'stay' | 'continue_background' | 'leave';

const EXECUTING_AI_SESSION_STAGE_STATUSES = new Set([
  'pending',
  'classifying',
  'running',
  'creating',
]);

export function resolveEpisodeSwitchConfirmAction(
  choice: EpisodeSwitchConfirmChoice
): 'none' | 'park' | 'exit' {
  switch (choice) {
    case 'stay':
      return 'none';
    case 'continue_background':
      return 'park';
    case 'leave':
      return 'exit';
    default: {
      const exhaustive: never = choice;
      return exhaustive;
    }
  }
}

export function collapseParseFlowGridForViewport(
  columns: string,
  viewportWidth: number | null,
  collapseBelow = 1100
): string {
  if (viewportWidth != null && viewportWidth < collapseBelow) {
    return 'minmax(0, 1fr)';
  }
  return columns;
}

/**
 * 切换单集时，判断当前 AI 助手工作区应该：
 * - 'none'：未真正切换到不同的一集；
 * - 'exit'：当前集有真实执行中的 AI 阶段，切集前让用户选择处理方式；
 * - 'switch'：当前没有执行中的 AI 阶段时换集，挂起当前集并恢复目标集会话；
 * - 'block'：仅拦截采纳后创建资产/分镜拆解会话的短暂过渡占位态
 *   （见 `isProtectedTransientAISessionStage`）。此时静默切集/退出会让占位态被清空、
 *   接口返回后无法再写回对应集。
 */
export function resolveScreenplaySelectAISessionAction({
  currentStage,
  currentProjectId,
  nextProjectId,
}: {
  currentStage: ActiveAISessionStage | null | undefined;
  currentProjectId: string | null | undefined;
  nextProjectId: string | null | undefined;
}): ScreenplaySelectAISessionAction {
  const hasEpisodeIds = Boolean(currentProjectId && nextProjectId);
  if (!hasEpisodeIds) return 'none';
  if (currentProjectId === nextProjectId) return 'none';
  if (isProtectedTransientAISessionStage(currentStage)) return 'block';
  if (
    currentStage &&
    EXECUTING_AI_SESSION_STAGE_STATUSES.has(String(currentStage.stageStatus ?? ''))
  ) {
    return 'exit';
  }
  return 'switch';
}

export type CreatedEpisodeWorkspaceRelease = 'park-previous' | 'clear' | 'none';

/** 新集尚无会话；创建完成后必须释放残留 stage，让启动器重新挂载。 */
export function resolveCreatedEpisodeWorkspaceRelease({
  previousProjectId,
  createdProjectId,
}: {
  previousProjectId: string | null | undefined;
  createdProjectId: string | null | undefined;
}): CreatedEpisodeWorkspaceRelease {
  if (!createdProjectId) return 'none';
  if (previousProjectId && previousProjectId !== createdProjectId) return 'park-previous';
  return 'clear';
}

export function parkAndRestoreEpisodeAISessionStage({
  parkedStages,
  fromProjectId,
  toProjectId,
  currentStage,
}: {
  parkedStages: Record<string, ActiveAISessionStage>;
  fromProjectId: string;
  toProjectId: string;
  currentStage: ActiveAISessionStage | null;
}): {
  parkedStages: Record<string, ActiveAISessionStage>;
  activeStage: ActiveAISessionStage | null;
} {
  const nextParked = { ...parkedStages };
  if (currentStage && !isCompletedStoryboardStage(currentStage)) {
    nextParked[fromProjectId] = currentStage;
  } else {
    delete nextParked[fromProjectId];
  }
  const restoredStage = nextParked[toProjectId] ?? null;
  // 恢复后由 activeStage 持有状态，避免缓存仍保留恢复前的草稿副本。
  delete nextParked[toProjectId];
  return {
    parkedStages: nextParked,
    activeStage: isCompletedStoryboardStage(restoredStage) ? null : restoredStage,
  };
}

export function resolveParseFlowLayoutState({
  viewMode,
  activeScreenplayId,
  screenplayListCount,
  initialScript,
  shotsCount,
}: ResolveParseFlowLayoutStateParams): ParseFlowLayoutState {
  const isStoryboardWorkspaceMode = viewMode === 'preview' || viewMode === 'custom';
  const hasAnyScreenplay =
    !!activeScreenplayId || screenplayListCount > 0 || !!initialScript.trim();
  const hasAnyShots = shotsCount > 0;
  // 当前没有任何业务需求要在非 entry 场景隐藏剧本预览栏。
  const showScriptPreviewPanel = viewMode !== 'entry';
  const showStoryboardListPanel = viewMode !== 'entry';
  const showStoryboardEditPanel = isStoryboardWorkspaceMode && hasAnyShots;

  const footerButtonConfig: FooterButtonConfig =
    viewMode === 'entry'
      ? { show: false, text: '', disabled: false }
      : {
          show: isStoryboardWorkspaceMode,
          text: '完成',
          disabled: !hasAnyShots,
          tooltip: '完成当前分镜并返回项目',
        };

  const columns: string[] = ['280px'];

  if (showScriptPreviewPanel) {
    columns.push('1fr');
  }

  if (showStoryboardListPanel) {
    columns.push(showScriptPreviewPanel ? '50%' : '1fr');
  }

  return {
    isStoryboardWorkspaceMode,
    hasAnyScreenplay,
    hasAnyShots,
    showScriptPreviewPanel,
    showStoryboardListPanel,
    showStoryboardEditPanel,
    footerButtonConfig,
    showDialogFooter: footerButtonConfig.show,
    dialogGridTemplateColumns: columns.join(' '),
  };
}
